// LLM-facing parameter schema for {@link TransformDatasetTool}.
//
// Pattern: `{ expression, outputDataset }`. The LLM writes a free-form
// s-expression that evaluates to a `Dataset`; any dataset bound by an
// earlier producer call on this branch is referenceable by name
// directly inside the expression (e.g. `(filter top-wells …)`) —
// the resolver walks the active branch's recipes and re-derives the
// value on demand (`useBindingScope` / `useThreadScopedEval`). The
// result is bound under `outputDataset` purely by this tool-call part
// on the transcript. Nothing is staged or persisted separately; a
// failed/streaming call simply isn't a producer. Reusing an
// `outputDataset` name shadows the earlier dataset on this branch
// (last successful producer wins).

import { z } from 'zod';

import { expressionSchema, outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetSummary } from '#plugin-chat/lib/tool/dataset-summary.ts';
import type { ToolResult } from '#plugin-chat/lib/transport/types.ts';

export const TransformDatasetInputSchema = z.object({
  expression: expressionSchema.describe(
    'An s-expression that evaluates to a Dataset. Typically opens with a top-level transform op like `(filter <bound-dataset-name> #(<predicate>))`, `(aggregate <bound-dataset-name> …)`, or a threaded pipeline `(-> <bound-dataset-name> (filter …) (sort …))`, where `<bound-dataset-name>` is the binding name of any dataset produced by an earlier tool call on this branch; any expression returning a Dataset is accepted. The expression captures the operation declaratively — materialisation is deferred until a downstream consumer reads rows.',
  ),
  outputDataset: outputDatasetSchema,
});

/** @public */
export type TransformDatasetInput = z.infer<typeof TransformDatasetInputSchema>;

/**
 * Schema-summary envelope returned from the tool's `execute` to the LLM.
 *
 * Eval is eager (no I/O — verbs propagate the schema synchronously), so
 * the LLM receives the resolved column list + types immediately and can
 * chain further tool calls confidently. Materialised rows are fetched
 * lazily at consumption time (the tool's `render` calls
 * `useMaterializedExpr(args.expression)`).
 *
 * @public
 */
export type TransformDatasetOutput = Readonly<{
  /** Success discriminator — every tool success envelope carries this
   * (matches `ToolOutputSuccessSchema`); failures carry `success: false`
   * via the structured error envelope instead. */
  success: true;
  /** Discriminator (`'lazy' | 'materialized'`) from the resulting Dataset. */
  state: string;
  /** Echoed `outputDataset` — the name this result is bound under. */
  outputDataset: string;
  /** Column-list shape: `[ { name, kind }, … ]`. Matches the engine's
   * `Schema = ReadonlyMap<string, ColumnType>` after a serialisation pass. */
  schema: readonly Readonly<{ name: string; kind: string }>[];
  summary: DatasetSummary;
}> &
  ToolResult;
